import { useEffect, useMemo } from "react";
import styled, { css } from "styled-components";
import { FantasyBadge } from "../../../components/Badge";
import { Flex } from "../../../components/Grid";
import { Icon } from "../../../components/Icon";
import { Caption1, Subhead } from "../../../components/Typography";
import { Equaliser } from "../../../components/progress/Equaliser";
import { DropEntry, VariantFile } from "../../../dropTypes";
import { useAudio } from "../../../hooks/media";
import {
  hideAboveMobile,
  hideOnMobile,
  smallScreenAndBelow,
  xSmallScreen,
} from "../../../lib/mediaQuery";
import { ThumbnailContainer } from "../dropImage";

interface PreviewProps {
  drop?: DropEntry;
}

export const ThumbnailAudioPreview = ({ drop }: PreviewProps) => {
  const audioPreviewFiles: VariantFile[] = useMemo(
    () =>
      drop?.variants?.[0].collectionMetadata?.files.filter((file) =>
        file?.preview?.mime?.startsWith("audio")
      ),
    [drop]
  );

  const previewUrls: string[] = useMemo(
    () => audioPreviewFiles?.map((file) => file.preview?.uri),
    [audioPreviewFiles]
  );

  const { state, currentTrack, togglePlayback, stopPlayback, tracksLoaded } =
    useAudio(previewUrls);

  const handleClickPlayback = (event) => {
    event?.preventDefault();
    togglePlayback();
  };

  useEffect(() => {
    const stopAllOtherPlaylists = () => {
      if (previewUrls.length > 0) {
        stopPlayback();
      }
    };
    document.addEventListener("audioPlaying", stopAllOtherPlaylists);
    return () =>
      document.removeEventListener("audioPlaying", stopAllOtherPlaylists);
  }, []);

  const trackName: string = useMemo(
    () =>
      audioPreviewFiles?.find(
        (file) => file?.preview?.uri === previewUrls[currentTrack]
      )?.metadata?.trackName,
    [currentTrack, audioPreviewFiles]
  );
  const isFantasy = drop?.filters?.includes("fantasy_manager_true");

  const handleSkipTrack = (event, direction) => {
    event?.preventDefault();
    const directionInt = currentTrack + (direction === "next" ? 1 : -1);
    togglePlayback(directionInt);
  };

  return (
    <PreviewContainer
      justify={"center"}
      direction={"column"}
      data-is-playing={state === "playing"}
    >
      {isFantasy && (
        <FantasyBadge
          css={`
            position: absolute;
            top: var(--spacing-xs);
            left: var(--spacing-xs);
            z-index: 2;
            [data-is-playing="true"] & {
              display: none;
            }
          `}
        />
      )}
      <TrackName trackName={trackName} />
      {(state === "idle" || state === "paused" || state === "ended") &&
        previewUrls.length > 0 && <PlayButton onClick={handleClickPlayback} />}
      {state === "playing" && <PauseButton onClick={handleClickPlayback} />}
      {previewUrls.length > 1 && (
        <SkipTrack
          handleSkipTrack={handleSkipTrack}
          prevDisabled={currentTrack === 0}
          nextDisabled={
            currentTrack + 1 === previewUrls.length ||
            !tracksLoaded[currentTrack]
          }
        />
      )}
    </PreviewContainer>
  );
};

interface Props {
  onClick: (e: any) => void;
}

interface TrackProps {
  trackName: string;
}

const TrackName = ({ trackName }: TrackProps) => {
  return (
    <CurrentTrack>
      <EqualiserContainer>
        <Equaliser css={hideOnMobile} size={16} />
        <Equaliser css={hideAboveMobile} size={12} />
      </EqualiserContainer>
      <Subhead
        css={`
          ${hideOnMobile};
          ${trackOpacity};
          ${ellipses};
        `}
      >
        {trackName}
      </Subhead>
      <Caption1
        css={`
          ${hideAboveMobile};
          ${trackOpacity};
          ${ellipses};
        `}
      >
        {trackName}
      </Caption1>
    </CurrentTrack>
  );
};

const ellipses = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: var(--spacing-xxs);
`;

const PlayButton = ({ onClick }: Props) => {
  return (
    <ButtonContainer onClick={onClick}>
      <Icon name={"Play"} height={30} width={30} color={"black"} />
    </ButtonContainer>
  );
};

const PauseButton = ({ onClick }: Props) => {
  return (
    <ButtonContainer onClick={onClick}>
      <Icon name={"Pause"} height={30} width={30} color={"black"} />
    </ButtonContainer>
  );
};

interface SkipTrackProps {
  handleSkipTrack: (event: any, direction: "next" | "prev") => void;
  prevDisabled: boolean;
  nextDisabled: boolean;
}

const SkipTrack = ({
  handleSkipTrack,
  prevDisabled,
  nextDisabled,
}: SkipTrackProps) => {
  return (
    <ChangeTrackContainer onClick={(e) => e.preventDefault()}>
      <SkipTrackIcon
        onClick={(event) => handleSkipTrack(event, "prev")}
        disabled={prevDisabled}
        data-is-disabled={prevDisabled}
      >
        <Icon name={"PrevTrack"} color={"white"} height={16} width={16} />
      </SkipTrackIcon>
      <SkipTrackIcon
        onClick={(event) => handleSkipTrack(event, "next")}
        disabled={nextDisabled}
        data-is-disabled={nextDisabled}
      >
        <Icon name={"NextTrack"} color={"white"} height={16} width={16} />
      </SkipTrackIcon>
    </ChangeTrackContainer>
  );
};

const trackOpacity = css`
  opacity: 0;
  [data-is-playing="true"] & {
    opacity: 1;
    transition: opacity var(--transition-speed-normal) var(--easing-standard);
  }
`;

const PreviewContainer = styled(Flex)`
  position: absolute;
  padding-bottom: 100%;
  width: 100%;
  z-index: var(--depth-higher);
`;

const EqualiserContainer = styled.div`
  display: none;
  position: relative;
  bottom: 2px;
  margin: 0 var(--spacing-xxs) 0;
  [data-is-playing="true"] & {
    display: block;
  }
  ${xSmallScreen(css`
    bottom: var(--spacing-xxs);
  `)}
`;

const CurrentTrack = styled.div`
  position: absolute;
  top: 0;
  margin: 0 var(--spacing-sm);
  border-radius: 500px;
  width: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: var(--spacing-sm);
  background-color: rgba(1, 1, 1, 0);
  padding: 2px;
  [data-is-playing="true"] & {
    background-color: rgba(1, 1, 1, 0.75);
    transition: background-color var(--transition-speed-normal)
      var(--easing-standard);
  }
`;

const ButtonContainer = styled.div`
  display: none;
  justify-content: center;
  align-items: center;
  position: absolute;
  background: white;
  border-radius: 15%;
  bottom: 0;
  left: 0;
  height: 48px;
  width: 48px;
  margin: var(--spacing-sm);
  ${ThumbnailContainer}:hover & {
    display: flex;
    opacity: 0.5;
  }
  [data-is-playing="true"] & {
    border-radius: 50%;
    display: flex;
    opacity: 1;
    transition: opacity var(--transition-speed-normal) var(--easing-standard);
    ${ThumbnailContainer}:hover & {
      display: flex;
      opacity: 1;
    }
  }
  &&:hover {
    opacity: 1;
    transition: scale var(--transition-speed-normal) var(--easing-standard);
    scale: 1.1;
  }
  ${smallScreenAndBelow(
    css`
      height: 38px;
      width: 38px;
      display: flex;
      opacity: 0.5;
    `
  )}
`;

const ChangeTrackContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  bottom: 0;
  right: 0;
  margin: var(--spacing-sm);
`;

const SkipTrackIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: black;
  margin: 0 var(--spacing-xxs);
  border-radius: 50%;
  height: 38px;
  width: 38px;
  opacity: 0;
  [data-is-playing="true"] & {
    opacity: 1;
    transition: opacity var(--transition-speed-normal) var(--easing-standard);
    ${ThumbnailContainer}:hover &:not(:disabled) {
      opacity: 1;
      transition: opacity var(--transition-speed-normal) var(--easing-standard);
    }
    :disabled {
      opacity: 0.5;
    }
  }
  @media (pointer: fine) {
    &:not(:disabled):hover {
      transition: scale var(--transition-speed-normal) var(--easing-standard);
      scale: 1.1;
    }
  }
  &:not(:disabled):active {
    transition: scale var(--transition-speed-normal) var(--easing-standard);
    scale: 0.95;
  }
  ${xSmallScreen(
    css`
      height: 28px;
      width: 28px;
      &:disabled {
        [data-is-playing="true"] & {
          opacity: 0.5;
        }
      }
    `
  )}
`;
